<template>
  <div class="user">
    <avatar-viewer v-if="gravatar"
                   :img='gravatar'
                   class="avatar"
                   :width='180'
                   :height='180'
                   :radius='90'
                   :show-upload="false" />
    <flashbase-default-avatar v-else
                              class="avatar" />
    <avatar class='avatar-viewer'
            :width='180'
            :height='180'
            :radius='90'
            :title="$t('AccountInfo.AccountInfoUser.uploadAvatar')"
            :preview-hint="$t('AccountInfo.AccountInfoUser.previewHint')"
            :crop-hint="$t('AccountInfo.AccountInfoUser.cropHint')"
            :select-img-hint="$t('AccountInfo.AccountInfoUser.selectImgHint')"
            :ok-text="$t('AccountInfo.AccountInfoUser.okText')"
            :cancel-text="$t('AccountInfo.AccountInfoUser.cancelText')"
            @avatar='getFile'>
      <template #controller>
        <a-button type="primary"
                  class="update-avatar">{{ $t('AccountInfo.AccountInfoUser.changeAvatar') }}</a-button>
      </template>
    </avatar>
    <div class="info">
      <div class="item">
        <shop-filled />
        <span class="name">{{ $t('AccountInfo.AccountInfoUser.shopName') }}</span>
        <span class="value">{{ user.username }}</span>
        <FormOutlined @click="editUsername" />
      </div>
      <div class="item">
        <mobile-filled />
        <span class="name">{{ $t('AccountInfo.AccountInfoUser.registerNumber') }}</span>
        <span class="value">{{ user.mobile || $t('AccountInfo.AccountInfoUser.unregisteredMobile') }}</span>
        <FormOutlined @click="editMobile" />
      </div>
      <div class="item">
        <clock-circle-filled />
        <span class="name">{{ $t('AccountInfo.AccountInfoUser.registerTime') }}</span>
        <span class="value">{{ registerAt }}</span>
      </div>
    </div>
  </div>
  <a-modal v-model:visible="showChangeUsername"
           :title="$t('AccountInfo.AccountInfoUser.editShopName')"
           @ok="onChangeUsername">
    <a-form ref="changeUsernameForm"
            :model="usernameFormState"
            :wrapper-col="{ span: 18 }"
            :label-col="{ span: 6 }"
            autocomplete="off">
      <a-form-item :label="$t('AccountInfo.AccountInfoUser.shopName')"
                   name="username"
                   :rules="[{ required: true, validator: validateUsername }]">
        <a-input v-model:value="usernameFormState.username"
                 :placeholder="$t('AccountInfo.AccountInfoUser.shopNamePlaceholder')"
                 @press-enter="onChangeUsername">
          <template #prefix>
            <shop-outlined />
          </template>
        </a-input>
      </a-form-item>
    </a-form>
  </a-modal>

  <a-modal v-model:visible="showChangeMobile"
           :title="$t('AccountInfo.AccountInfoUser.editMobile')"
           @ok="onChangeMobile">
    <a-form ref="changeMobileForm"
            :model="mobileFormState"
            :wrapper-col="{ span: 18 }"
            :label-col="{ span: 6 }"
            autocomplete="off">
      <a-form-item :label="$t('AccountInfo.AccountInfoUser.registerMobile')"
                   name="mobile"
                   :rules="[{ required: true, validator: validateMobile }]">
        <a-input v-model:value="mobileFormState.mobile"
                 autocomplete="off"
                 :placeholder="$t('AccountInfo.AccountInfoUser.mobilePlaceholder')">
          <template #prefix>
            <mobile-outlined />
          </template>
        </a-input>
      </a-form-item>
      <a-form-item :label="$t('AccountInfo.AccountInfoUser.captcha')"
                   name="captcha"
                   :rules="[{ required: true }]">
        <a-row :gutter="10">
          <a-col :span="16">
            <a-input v-model:value="mobileFormState.captcha"
                     autocomplete="off"
                     :placeholder="$t('AccountInfo.AccountInfoUser.captchaPlaceholder')">
              <template #prefix>
                <code-outlined />
              </template>
            </a-input>
          </a-col>
          <a-col :span="8"
                 class="captcha">
            <count-down-button html-type="submit"
                               :text="$t('AccountInfo.AccountInfoUser.captchaText')"
                               :counting-text-post="$t('AccountInfo.AccountInfoUser.captchaCountingTextPost')"
                               :disabled="!mobileFormState.isMobileValid"
                               @click="getCaptcha"></count-down-button>
          </a-col>
        </a-row>
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script setup lang="ts">
import { CORE_AXIOS, CORE_USER, Http, type UserService } from '@/core'
import { toUrl } from '@/utils'
import { Avatar, AvatarViewer, CountDownButton } from '@iota-cn/ui-components'
import { Validator } from '@iota-cn/util-validation'
import { message } from 'ant-design-vue'
import type { Rule } from 'ant-design-vue/lib/form'
import dayjs from 'dayjs'
import { container } from 'tsyringe'
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const axios = container.resolve<Http>(CORE_AXIOS)
const userService = container.resolve<UserService>(CORE_USER)
const user = userService.getUser()
const { t } = useI18n()

const registerAt = computed(() => {
  return dayjs(user.value.createdAt).format("YYYY-MM-DD HH:mm:ss")
})

const gravatar = computed(() => {
  return toUrl(user.value.gravatar)
})

// 修改头像
const getFile = (canvas: HTMLCanvasElement) => {
  canvas.toBlob((blob: Blob | null) => {
    if (blob) {
      const fd = new FormData()
      let gravatar: string
      fd.append("fileBlob", blob, `${user.value.username}.png`) // 目前后端做的验证必须加后缀
      axios.post('/v1/api/attachment/gravatar', fd)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((res: any) => {
          gravatar = res.uploaded
          return axios.put('/v1/api/accounts/profile', {
            gravatar
          })
        }).then(() => {
          userService.setUser({ user: { gravatar } })
        }).catch(() => {

        })
    }
  })
}

// 修改店铺名称
const usernameFormState = reactive({
  username: ''
})
const changeUsernameForm = ref()
const showChangeUsername = ref<boolean>(false)
const editUsername = () => {
  showChangeUsername.value = true
}
const onChangeUsername = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  changeUsernameForm.value.validateFields().then((values: any) => {
    if (user.value.username === values.username) {
      showChangeUsername.value = false
      return
    }
    axios.put(`/v1/api/accounts/profile`, values).then(() => {
      userService.setUser({ user: values })
      showChangeUsername.value = false
      message.success(t('AccountInfo.AccountInfoUser.shopNameSuccess'))
    }).catch(() => {
      message.error(t('AccountInfo.AccountInfoUser.shopNameFailed'))
    })
  }).catch(() => {

  })
}
const validateUsername = async (_rule: Rule, value: string) => {
  if (user.value.username === value) {
    Promise.resolve()
  }
  if (Validator.isUserName(value)) {
    return axios.get(`/v1/api/validations/usernames/validate?username=${value}`).then(() => {
      return Promise.resolve()
    }).catch(() => {
      return Promise.reject(t('AccountInfo.AccountInfoUser.shopNameError'))
    })
  } else {
    return Promise.reject(t('AccountInfo.AccountInfoUser.shopNameError'))
  }
}

// mobile
const mobileFormState = reactive({
  mobile: '',
  captcha: '',
  isMobileValid: false
})
const changeMobileForm = ref()
const showChangeMobile = ref<boolean>(false)
const editMobile = () => {
  showChangeMobile.value = true
}
const getCaptcha = async () => {
  const mobile = mobileFormState.mobile
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axios.post(`/v1/api/fb/validations/smses`, { mobile }).then((res: any) => {
    if (res.status === 403) {
      message.error(t('AccountInfo.AccountInfoUser.captchaError'))
    }
  })
}

const onChangeMobile = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  changeMobileForm.value.validateFields().then((values: any) => {
    if (user.value.mobile === values.mobile) {
      showChangeMobile.value = false
      return
    }
    const mobile = { mobile: values.mobile }
    axios.put(`/v1/api/accounts/profile?captcha=${values.captcha}`, mobile).then(() => {
      userService.setUser({ user: mobile })
      showChangeMobile.value = false
      message.success(t('AccountInfo.AccountInfoUser.mobileSuccess'))
    }).catch(() => {
      message.error(t('AccountInfo.AccountInfoUser.mobileFailed'))
    })
  }).catch(() => {

  })
}

const validateMobile = async (_rule: Rule, value: string) => {
  if (user.value.mobile === value) {
    Promise.resolve()
  }
  if (Validator.isPhone(value)) {
    return axios.get(`/v1/api/validations/mobiles/validate?mobile=${value}`)
      .then(() => {
        mobileFormState.isMobileValid = true
        return Promise.resolve()
      }).catch(() => {
        mobileFormState.isMobileValid = false
        return Promise.reject(t('AccountInfo.AccountInfoUser.unusedMobileError'))
      })
  } else {
    mobileFormState.isMobileValid = false
    return Promise.reject(t('AccountInfo.AccountInfoUser.invalidMobileError'))
  }
}
</script>




<style lang="less" scoped>
.user {
  padding: 150px 55px;
  display: flex;
  flex-direction: column;
  align-items: center;

  .avatar {
    width: 180px !important;
    height: 180px !important;
  }

  .update-avatar {
    width: 120px;
    height: 40px;
    font-size: 14px;
    font-family: 'Microsoft YaHei';
    font-weight: 400;
    margin: 30px;
  }

  .info {
    display: flex;
    flex-direction: column;
    padding-top: 80px;
    padding-bottom: 10px;

    .item {
      flex-basis: 33%;
      margin: 8px;

      span {
        color: #999999;
      }

      .name {
        font-size: 14px;
        font-family: 'Microsoft YaHei';
        font-weight: 400;
        color: #999999;
        margin-right: 8px;
        margin-left: 8px;
      }

      .value {
        font-size: 14px;
        font-family: 'Microsoft YaHei';
        font-weight: 400;
        color: #111111;
        margin-right: 8px;
      }
    }
  }
}
</style>
